import { useCallback, useMemo, useState } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import CloseIcon from '@mui/icons-material/Close';
import { Alert, Button, CircularProgress, IconButton } from '@mui/material';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import { useSnackbar } from 'notistack';
import { useForm } from 'react-hook-form';
import * as Yup from 'yup';

import { ControlledTextField } from '../../../../components/FormControlsV2/ControlledTextField';
import { FormProvider } from '../../../../components/FormControlsV2/FormProvider';
import { ERRORS_TEXTS } from '../../../../const/validation';
import { faqApi } from '../../../../effector/faq';
import { Category } from '../../../../types/faq';
import { getMessageFromFailedRequest } from '../../../../utils';

type FormFields = {
  name: string;
  friendlyUrl: string;
};

const schema = Yup.object({
  name: Yup.string().default('').required(ERRORS_TEXTS.required),
  friendlyUrl: Yup.string().default(''),
});

interface Props {
  category?: Category | null;
  onClose: () => void;
  onUpdate: () => void;
}

export const CategoryDialog: React.FC<Props> = ({ category, onClose, onUpdate }) => {
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { enqueueSnackbar } = useSnackbar();

  const isEdit = !!category;

  const defaultValues = useMemo<FormFields>(
    () => ({
      name: category?.name ?? '',
      friendlyUrl: category?.friendlyUrl ?? '',
    }),
    [category],
  );

  const {
    control,
    handleSubmit,
    formState: { errors, isDirty },
  } = useForm<FormFields>({
    defaultValues,
    mode: 'onChange',
    resolver: yupResolver(schema),
  });

  const handleError = useCallback((e: Error) => {
    const message = getMessageFromFailedRequest(e);
    setError(message);
  }, []);

  const onSubmit = useCallback(
    async (values: FormFields) => {
      setError('');
      setIsLoading(true);

      try {
        if (isEdit) {
          await faqApi.editCategory({ ...values, id: category!.id });
          enqueueSnackbar('Категория обновлена');
        } else {
          await faqApi.addCategory(values);
          enqueueSnackbar('Категория добавлена');
        }
        onUpdate();
      } catch (e) {
        handleError(e as Error);
      } finally {
        setIsLoading(false);
      }
    },
    [isEdit, category, enqueueSnackbar, onUpdate, handleError],
  );

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle align="center">{isEdit ? 'Редактирование категории' : 'Добавление категории'}</DialogTitle>
      <IconButton
        aria-label="close"
        onClick={onClose}
        sx={(theme) => ({
          position: 'absolute',
          right: 8,
          top: 8,
          color: theme.palette.grey[500],
        })}
      >
        <CloseIcon />
      </IconButton>
      <DialogContent>
        <form onSubmit={handleSubmit(onSubmit)} noValidate>
          <FormProvider control={control} errors={errors}>
            <ControlledTextField name="name" label="Название" />
            <ControlledTextField name="friendlyUrl" label="Url" />
          </FormProvider>

          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}

          <Button
            type="submit"
            variant="contained"
            color="primary"
            disabled={!isDirty || isLoading}
            fullWidth
            sx={{ mt: 2 }}
            startIcon={isLoading ? <CircularProgress size={20} /> : undefined}
          >
            Сохранить
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};
